import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Wallet, MousePointerClick, Target, TrendingUp,
  Inbox,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, cn } from '@/lib/utils';
import { KpiCard } from '@/components/ui/KpiCard';
import { ProgressRing } from '@/components/ui/ProgressRing';
import { StatusBadge } from '@/components/ui/Badge';
import { PageSkeleton, CardSkeleton, TableRowSkeleton } from '@/components/ui/Skeleton';
import { HealthScoreWidget } from '@/components/Insights/HealthScoreWidget';
import { BudgetPacingWidget } from '@/components/Insights/BudgetPacingWidget';
import { BiddingRecommendationCard } from '@/components/Insights/BiddingRecommendationCard';
import { AnomalyAlertBadge } from '@/components/Insights/AnomalyAlertBadge';
import { HealthTimelineChart } from '@/components/Insights/HealthTimelineChart';
import { useHealthScore, useBudgetPacing, useBiddingRecommendation, useAnomalyDetect } from '@/hooks/useInsights';
import type {
  KpiResponse,
  CampaignRow,
  AccountTimelineResponse,
  Account,
} from '@/lib/api-types';

/**
 * Clamp a value to [min, max] — used by the health score formula below so
 * a single outlier metric can't push the overall score above 100 or below 0.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Derive a simple 0–100 health score from KPI totals.
 *
 * Returns ``null`` when any of the inputs required for the formula are missing
 * — the caller hides the widget entirely instead of showing a "0%" ring that
 * would be indistinguishable from an actually-failing account.
 *
 * The weights are a first-pass heuristic (ROAS 40% / CTR 30% / conv-rate 30%)
 * and deliberately benchmark against common e-commerce defaults. Phase 2 will
 * replace this with per-vertical benchmarks pulled from the worker.
 */
function computeHealthScore(totals?: {
  roas?: number;
  ctr?: number;
  clicks?: number;
  conversions?: number;
}): number | null {
  if (!totals) return null;
  const { roas, ctr, clicks, conversions } = totals;
  if (
    roas === undefined ||
    ctr === undefined ||
    clicks === undefined ||
    conversions === undefined
  ) {
    return null;
  }
  // ROAS score: 400% = full marks, 0% = 0 pts.
  const roasScore = clamp((roas / 400) * 100, 0, 100);
  // CTR score: 5% CTR = full marks (Google Ads API reports CTR as 0.05).
  const ctrScore = clamp((ctr / 0.05) * 100, 0, 100);
  // Conversion rate score: 5% conv-rate = full marks.
  const convRate = clicks > 0 ? conversions / clicks : 0;
  const convRateScore = clamp((convRate / 0.05) * 100, 0, 100);

  return Math.round(roasScore * 0.4 + ctrScore * 0.3 + convRateScore * 0.3);
}

/**
 * Format an ISO date (YYYY-MM-DD) as a short Polish label like "13 kwi" —
 * used on the X-axis of the timeline area chart.
 */
function formatDayLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

/**
 * Format a mutation's ``created_at`` timestamp for the Ostatnia aktywność
 * list. Falls back to the raw string on parse failure so we never render
 * "Invalid Date".
 */
function formatRelativeDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AccountDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const accountId = id ? parseInt(id, 10) : 0;

  const { data: account, isLoading: loadingAccount } = useQuery<Account>({
    queryKey: ['account', id],
    queryFn: async () => (await api.get(`/accounts/${id}`)).data.data,
    enabled: Boolean(id),
  });

  const { data: healthScoreData, isLoading: loadingHealth } = useHealthScore(accountId);
  const { data: budgetPacingData, isLoading: loadingPacing } = useBudgetPacing(accountId);
  const { data: biddingRec, isLoading: loadingBidding } = useBiddingRecommendation(accountId);
  const { data: anomalies, isLoading: loadingAnomalies } = useAnomalyDetect(accountId);

  const { data: kpi, isLoading: loadingKpi } = useQuery<KpiResponse>({
    queryKey: ['account', id, 'kpi'],
    queryFn: async () => (await api.get(`/accounts/${id}/kpi?days=30`)).data.data,
    enabled: Boolean(id),
  });

  const { data: campaignsRes, isLoading: loadingCampaigns } = useQuery<{ data: CampaignRow[] }>({
    queryKey: ['account', id, 'campaigns'],
    queryFn: async () => (await api.get(`/accounts/${id}/campaigns?days=30`)).data.data,
    enabled: Boolean(id),
  });

  const { data: timeline, isLoading: loadingTimeline } = useQuery<AccountTimelineResponse>({
    queryKey: ['account', id, 'timeline'],
    queryFn: async () => (await api.get(`/accounts/${id}/timeline?days=30`)).data.data,
    enabled: Boolean(id),
  });

  if (loadingAccount) return <PageSkeleton />;
  if (!account) return <div className="container py-8">{t('common.loading')}</div>;

  const totals = kpi?.totals;
  const currency = account.currency || 'PLN';

  const daily = timeline?.daily ?? [];
  const events = timeline?.events ?? [];

  // Chart-friendly series built once per render; the label is re-derived from
  // the ISO date so we never duplicate the server's formatting logic.
  const chartData = daily.map((d) => ({
    day: formatDayLabel(d.date),
    cost: d.cost,
    conversions: d.conversions,
  }));

  const costSpark = daily.map((d) => d.cost);
  const convSpark = daily.map((d) => d.conversions);

  // Sorted campaigns: cost desc with a copy (don't mutate react-query cache).
  const campaigns = (campaignsRes?.data ?? [])
    .slice()
    .sort((a, b) => (b.cost ?? 0) - (a.cost ?? 0));

  const healthScore = computeHealthScore(totals);

  return (
    <div>
      {healthScore !== null && (
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <ProgressRing value={healthScore} size={44} strokeWidth={4} color="#2E5BFF" />
            <div>
              <p className="text-xs text-slate-500">Health</p>
              <p className="text-sm font-semibold dark:text-white">{healthScore}%</p>
            </div>
          </div>
          <AnomalyAlertBadge data={anomalies} isLoading={loadingAnomalies} />
        </div>
      )}
      {healthScore === null && (
        <div className="flex justify-end mb-4">
          <AnomalyAlertBadge data={anomalies} isLoading={loadingAnomalies} />
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {loadingKpi || !totals ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : (
          <>
            <KpiCard
              label={t('kpi.cost')}
              value={totals.cost}
              formatter={(n) => formatCurrency(n, currency)}
              sparkData={costSpark}
              icon={<Wallet size={18} />}
              color="#2E5BFF"
            />
            <KpiCard
              label={t('kpi.clicks')}
              value={totals.clicks}
              formatter={(n) => formatNumber(n)}
              sparkData={costSpark}
              icon={<MousePointerClick size={18} />}
              color="#00C2A8"
            />
            <KpiCard
              label={t('kpi.conversions')}
              value={totals.conversions}
              formatter={(n) => formatNumber(n)}
              sparkData={convSpark}
              icon={<Target size={18} />}
              color="#F5A524"
            />
            <KpiCard
              label={t('kpi.roas')}
              value={totals.roas}
              formatter={(n) => `${Math.round(n)}%`}
              sparkData={convSpark}
              icon={<TrendingUp size={18} />}
              color="#8B5CF6"
            />
          </>
        )}
      </div>

      {/* Insights widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <HealthScoreWidget data={healthScoreData} isLoading={loadingHealth} />
        <BudgetPacingWidget data={budgetPacingData} isLoading={loadingPacing} currency={account?.currency || 'PLN'} />
        <BiddingRecommendationCard data={biddingRec} isLoading={loadingBidding} />
      </div>

      {/* rpt_ : Health score history timeline */}
      <div className="mb-8">
        <HealthTimelineChart accountId={accountId} days={90} />
      </div>

      {/* Trend chart */}
      <div className="grid grid-cols-1 gap-4 mb-8">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold dark:text-white">Trend wydatków i konwersji</h3>
            <span className="text-xs text-slate-500">Ostatnie 30 dni</span>
          </div>
          {loadingTimeline ? (
            <div className="h-[260px] flex items-center justify-center text-xs text-slate-400">
              {t('common.loading')}
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-slate-400">
              <Inbox size={32} />
              <p className="text-xs">Brak danych dziennych w tym okresie.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="detailCostGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2E5BFF" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#2E5BFF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="detailConvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00C2A8" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#00C2A8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={4} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={45} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={35} />
                <Tooltip
                  contentStyle={{
                    background: '#ffffff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '10px',
                    fontSize: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    color: '#1F2937',
                  }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="cost"
                  stroke="#2E5BFF"
                  fill="url(#detailCostGrad)"
                  strokeWidth={2}
                  name={`Wydatki (${currency})`}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="conversions"
                  stroke="#00C2A8"
                  fill="url(#detailConvGrad)"
                  strokeWidth={2}
                  name="Konwersje"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Campaign performance table + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Campaigns table */}
        <div className="card lg:col-span-2">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold dark:text-white">Top kampanie</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <th className="text-left p-3 font-medium">Kampania</th>
                  <th className="text-right p-3 font-medium">Wydatki</th>
                  <th className="text-right p-3 font-medium">Kliknięcia</th>
                  <th className="text-right p-3 font-medium">Konwersje</th>
                  <th className="text-right p-3 font-medium">CTR</th>
                  <th className="text-right p-3 font-medium">CPA</th>
                  <th className="text-right p-3 font-medium">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loadingCampaigns &&
                  Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={7} />)}

                {!loadingCampaigns && campaigns.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-sm text-slate-500">
                      Brak kampanii z wydatkami w tym okresie.
                    </td>
                  </tr>
                )}

                {campaigns.map((c) => {
                  const cpa = c.conversions > 0 ? c.cost / c.conversions : 0;
                  const roas = c.cost > 0 ? (c.conversion_value / c.cost) * 100 : 0;
                  return (
                    <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium dark:text-white truncate max-w-[220px]">{c.name}</span>
                          {c.channel_type && (
                            <span className="text-[10px] text-slate-400 uppercase">
                              {c.channel_type.replace('_', ' ')}
                            </span>
                          )}
                          <StatusBadge status={c.status.toLowerCase()} />
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-300">
                        {formatCurrency(c.cost, currency)}
                      </td>
                      <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-300">
                        {formatNumber(c.clicks)}
                      </td>
                      <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-300">
                        {formatNumber(Math.round(c.conversions))}
                      </td>
                      <td className="p-3 text-right font-mono text-slate-500">
                        {(c.ctr * 100).toFixed(2)}%
                      </td>
                      <td className="p-3 text-right font-mono text-slate-500">
                        {cpa > 0 ? formatCurrency(cpa, currency) : '—'}
                      </td>
                      <td className="p-3 text-right">
                        <span
                          className={cn(
                            'font-semibold',
                            roas >= 500
                              ? 'text-emerald-600'
                              : roas >= 300
                              ? 'text-amber-600'
                              : roas > 0
                              ? 'text-red-500'
                              : 'text-slate-400',
                          )}
                        >
                          {roas > 0 ? `${Math.round(roas)}%` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Timeline / recent mutations */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold dark:text-white mb-4">Ostatnia aktywność</h3>
          {loadingTimeline ? (
            <p className="text-xs text-slate-400">{t('common.loading')}</p>
          ) : events.length === 0 ? (
            <p className="text-xs text-slate-500">Brak zmian w tym okresie.</p>
          ) : (
            <ul className="space-y-3">
              {events.slice(0, 8).map((e) => (
                <li key={e.id} className="flex items-start gap-2 text-sm">
                  <span className="mt-1 h-2 w-2 rounded-full bg-brand-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium dark:text-white truncate">{e.title}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {e.type} · {formatRelativeDate(e.created_at)}
                    </p>
                  </div>
                  <StatusBadge status={e.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
